import { useCallback, useRef, useState } from 'react';

import { useMutation } from '@apollo/react-hooks';
import { noop } from 'lodash-es';

import { FireOrderIn, IServerOrder } from '@rbi-ctg/menu';
import {
  CommitOrderDocument as CommitOrderDocumentFulFillment,
  GetOrderDocument as GetOrderDocumentFulfillment,
  GetUserOrdersDocument as GetUserOrdersDocumentFulfillment,
} from 'generated/graphql-gateway';
import {
  CommitOrderDocument,
  GetOrderDocument,
  GetUserOrdersDocument,
} from 'generated/rbi-graphql';
import { useOrderStatus } from 'hooks/order-status';
import { ICommitOrderMutationResponse, ICommitOrderMutationVariables } from 'remote/queries/order';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { OrderSuccessFailureStatuses } from 'state/order/constants';
import { ServiceMode } from 'state/service-mode';
import { buildCommitDeliveryInput, buildCommitOrderInput } from 'utils/cart';

import { mapCardType } from './map-card-type';
import { ICommitOrderInput, IUseCommitOrderParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1500;

export const useCommitOrder = ({
  onCompleted = noop,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  rbiOrderId,
  skipOrderStatusPolling = true,
}: IUseCommitOrderParams) => {
  const [skipPolling, setSkipPolling] = useState(skipOrderStatusPolling);
  const { onCommitSuccess } = useOrderContext();
  const auth = useAuthContext();
  const commitOptions = useRef({} as ICommitOrderInput);

  const isFulfillmentServiceGetOrdersEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_FULFILLMENT_SERVICE_GET_ORDER
  );
  const isFulfillmentServiceGetUserOrdersEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_FULFILLMENT_SERVICE_GET_USER_ORDERS
  );

  const enableSkipRefetchRecentOrdersOnCommit = useFlag(
    LaunchDarklyFlag.ENABLE_SKIP_REFETCH_RECENT_ORDERS_ON_COMMIT
  );

  const refetchQueries: {
    query: any;
    variables?: any;
  }[] = [
    isFulfillmentServiceGetOrdersEnabled
      ? { query: GetOrderDocumentFulfillment, variables: { rbiOrderId } }
      : { query: GetOrderDocument, variables: { rbiOrderId } },
  ];

  // If flag is enabled, do not fetch recent orders right after an order is placed.
  if (!enableSkipRefetchRecentOrdersOnCommit) {
    const getUserOrdersQuery = isFulfillmentServiceGetUserOrdersEnabled
      ? { query: GetUserOrdersDocumentFulfillment }
      : { query: GetUserOrdersDocument };
    refetchQueries.push(getUserOrdersQuery);
  }

  const [fireMutation, mutationResult] = useMutation<
    ICommitOrderMutationResponse,
    ICommitOrderMutationVariables
  >(CommitOrderDocument, {
    awaitRefetchQueries: true,
    refetchQueries,
  });
  const [fireMutationFulfillment, mutationResultFulfillment] = useMutation<
    ICommitOrderMutationResponse,
    ICommitOrderMutationVariables
  >(CommitOrderDocumentFulFillment, {
    awaitRefetchQueries: true,
    refetchQueries,
  });
  const isFulfillmentServiceEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_FULFILLMENT_SERVICE_COMMIT_ORDER
  );

  const onSuccess = useCallback(
    (rbiOrder: IServerOrder) => {
      onCompleted();
      onCommitSuccess(rbiOrder);
    },
    [onCommitSuccess, onCompleted]
  );

  const queryResult = useOrderStatus({
    failureStatuses: OrderSuccessFailureStatuses.failures,
    onSuccess,
    orderStatusPollInterval,
    rbiOrderId,
    successStatuses: OrderSuccessFailureStatuses.success,
    skip: skipPolling,
  });

  const { orderStatus, serverOrder } = queryResult;

  const enableDriveThruOrderTimedFire = useFlag(LaunchDarklyFlag.ENABLE_DRIVE_THRU_TIMED_FIRE);

  const commitOrder = useCallback(
    async (options: ICommitOrderInput) => {
      commitOptions.current = options;
      const delivery = buildCommitDeliveryInput(options.order, auth.user);

      // If using Apple or Google Pay, get the underlying credit card used and set this as the card type used
      options.creditType = mapCardType(options);
      let fireOrderIn = (options.order.fireOrderIn || 0) as FireOrderIn;

      const isDriveThrough = options.order.serviceMode === ServiceMode.DRIVE_THRU;
      // for backwards compatibility purposes, we need to convert 0 to 1 for ASAP drive thru orders
      if (isDriveThrough && enableDriveThruOrderTimedFire && options.order.fireOrderIn === 0) {
        fireOrderIn = 1;
      }

      const input = buildCommitOrderInput({
        ...options,
        fireOrderIn,
        rbiOrderId: rbiOrderId!,
      });

      const skipCoolingPeriod = !!options.skipCoolingPeriod;
      const mutation = isFulfillmentServiceEnabled ? fireMutationFulfillment : fireMutation;
      const result = await mutation({
        variables: {
          delivery,
          input,
          skipCoolingPeriod,
        },
      });
      if (result.data) {
        setSkipPolling(false);
      }
      return result;
    },
    [
      auth.user,
      enableDriveThruOrderTimedFire,
      fireMutation,
      fireMutationFulfillment,
      isFulfillmentServiceEnabled,
      rbiOrderId,
    ]
  );

  const forceCommitOrder = useCallback(() => {
    return commitOrder({ ...commitOptions.current, skipCoolingPeriod: true });
  }, [commitOrder]);

  return {
    commitOrder,
    commitResult: isFulfillmentServiceEnabled ? mutationResultFulfillment : mutationResult,
    failure: queryResult.failure,
    forceCommitOrder,
    loading: mutationResult.loading || queryResult.loading || mutationResultFulfillment.loading,
    order: queryResult,
    orderStatus,
    serverOrder,
    success: queryResult.success,
  };
};
